import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import TooltipAvatar from '@/app/components/questionnaireV2/icons/tooltip-avatar';
import CloseIcon from '@/app/components/questionnaireV2/icons/close-icon';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

export function TooltipModal({ isOpen, onClose, children }: ModalProps) {
  const modalContent = (
    <div className="questionnaire-reset">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="tooltip-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
          >
            <motion.div
              className="tooltip-modal-content"
              initial={{ opacity: 0, y: 200 }}
              animate={{ opacity: 1, y: 200 }}
              exit={{ opacity: 0, y: 200 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="tooltip-modal-header">
                <button className="tooltip-modal-close-button" onClick={onClose} aria-label="Close modal">
                  <CloseIcon />
                </button>
              </div>
              <div className="flex justify-center items-center px-4 py-2">
                <TooltipAvatar />
              </div>

              {/* Modal Body */}
              <div className="tooltip-modal-body">{children}</div>
              <hr className="tooltip-modal-divider" />
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="tooltip-footer"
              >
                <button onClick={onClose} className="btn-tooltip-modal">
                  Close
                </button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // Render the modal at the document body level using a portal
  return createPortal(modalContent, document.body);
}

// Simple hook for modal state
export function useModal(initialOpen = false) {
  const [isOpen, setIsOpen] = useState(initialOpen);

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);
  const toggleModal = () => setIsOpen(!isOpen);

  return {
    isOpen,
    openModal,
    closeModal,
    toggleModal,
  };
}
